import React, { useEffect, useState } from "react";
import { GlobalService } from "../../../shared";
import Button from "../../../../components/ui/Button";
import Card from "../../../../components/ui/card";
import { Edit } from "lucide-react";
import { createSkills, getStaticSkills, getUserSkills, UpdateUserSkills, useAppDispatch } from "../../../../redux";
import { useSelector } from "react-redux";
import SkillsModal from "../../modals/SkillsModal";
import { LevelEnum, LevelLabelMap } from "../../../shared/model";
import { useTranslation } from "react-i18next";

export default function Skills({ userId, showEditIcon = true }: { userId?: string; showEditIcon?: boolean }) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { user } = useSelector((state: any) => state.auth);
    const { skills } = useSelector((state: any) => state.professional);
    const targetUserId = userId || user?.id; // Use passed userId or fallback to current user
    const [showModal, setShowModal] = useState(false);
    const [originalSkillsData, setoriginalSkillsData] = useState([]);
    const [allStaticSkills, setAllStaticSkills] = useState([]);

    const [skillsData, setSkillsData] = useState([]);
    useEffect(() => {
        getUserSkillsFromServer();
    }, [targetUserId]);

    const getUserSkillsFromServer = async () => {
        await dispatch(getUserSkills({ id: targetUserId }));
        const { payload } = await dispatch(getStaticSkills({}));
        setAllStaticSkills(payload?.data);
    };

    const handleSave = async (updatedSkills: any[]) => {
        const newItems = updatedSkills.filter(item => !item.id);
        const existingItems = updatedSkills.filter(item => item.id);
        let updatedCount = 0;

        // Update changed skills
        for (const item of existingItems) {
            const original = originalSkillsData.find(s => s.id === item.id);

            const originalLevel = LevelEnum[original?.level.toLowerCase() as keyof typeof LevelEnum];

            const hasChanged = item.name !== original?.name || Number(item.level) !== originalLevel;

            if (!hasChanged) continue;

            const updatePayload = {
                userId: { id: targetUserId },
                endorsements: 0,
                skillId: { id: item.skillId?.id },
                level: { id: Number(item.level) },
            };

            await dispatch(UpdateUserSkills({ id: item.id, data: updatePayload }));
            updatedCount++;
        }

        // Create new skills
        let createdItems: any[] = [];
        if (newItems.length > 0) {
            const createPayload = newItems.map(item => ({
                userId: { id: targetUserId },
                skillId: { id: item.skillId?.id },
                endorsements: 0,
                level: { id: item.level.id != undefined || null ? Number(item.level.id) : Number(item.level) },
            }));

            const result = await dispatch(createSkills({ data: createPayload }));

            if (createSkills.fulfilled.match(result)) {
                createdItems = result.payload;
            }
        }
        if (updatedCount > 0) {
            GlobalService.showToast("success", t("skills.successTitle"), t("skills.updateSuccess"));
        }
    };
    useEffect(() => {
        if (!skills) return;

        const transformed = skills.map((skill: any) => ({
            id: skill.id,
            name: skill.skillId?.name || "Unnamed Skill",
            endorsements: skill.endorsement || 0,
            level: LevelLabelMap[parseInt(skill.level?.id)] || "Unknown",
            skillId: { id: skill?.skillId?.id },
        }));

        setSkillsData(transformed);
        setoriginalSkillsData(transformed);
    }, [skills]);
    return (
        <>
            <Card color={undefined} className="p-6">
                <div className="flex justify-between items-start mb-4">
                    <h2 className="text-xl font-semibold">{t("skills.title")}</h2>
                    {showEditIcon && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-blue-600 hover:text-blue-700"
                            onClick={() => setShowModal(true)}>
                            <Edit className="w-4 h-4" />
                        </Button>
                    )}
                </div>
                <div className="space-y-4">
                    {skillsData.length > 0 &&
                        skillsData.map(skill => (
                            <div key={skill.id} className="flex items-center justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="font-medium text-gray-900">{skill.name}</span>
                                        <span className="text-blue-600 text-sm font-medium">
                                            {skill.endorsements} {t("skills.endorsements")}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-gray-500">{skill?.level}</span>
                                        <div className="flex-1 h-2 bg-gray-100 rounded-full">
                                            <div
                                                className="h-full bg-blue-600 rounded-full"
                                                style={{
                                                    width:
                                                        skill.level === LevelEnum.expert
                                                            ? "100%"
                                                            : skill.level === LevelEnum.advanced
                                                            ? "75%"
                                                            : skill.level === LevelEnum.intermediate
                                                            ? "50%"
                                                            : "25%",
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                </div>
            </Card>
            <SkillsModal
                open={showModal}
                skillsData={skillsData}
                handleSave={handleSave}
                allStaticSkills={allStaticSkills}
                onClose={() => setShowModal(false)}
            />
        </>
    );
}
